import { useCallback, useEffect, useRef } from "react";
import type { Controls, Step } from "react-joyride";
import {
	ANCHOR_ARRIVAL_MUTATIONS,
	anchorSelector,
	isAnchorVisible,
	WALKTHROUGH_ARRIVES_ON_ITS_OWN_ATTR,
	watchAnchor,
	whenAnchorSettles,
} from "./walkthroughAnchors";
import { walkthroughStepData } from "./walkthroughSteps";

interface Options {
	/** joyride's controls, captured from its first step event; null until a step has shown. */
	controls: Controls | null;
	steps: Step[];
	stepIndex: number;
}

/** Keys that activate a focused control: press (Enter/Space), and open a MUI Select (the arrows). */
const ACTIVATION_KEYS: ReadonlySet<string> = new Set(["Enter", " ", "ArrowUp", "ArrowDown"]);

/** Moves on only while joyride is still on `index`, so two triggers for one step can never skip the next. */
const advanceFrom = (controls: Controls, index: number): void => {
	if (controls.info().index === index) controls.next();
};

/**
 * The step changes the user's own action causes: opening what an opener step points at, and using or
 * dismissing a deferred step's control.
 *
 * Owned by the controller, not the tooltip. joyride renders a step only while its target is in the DOM, so
 * the first re-render after that control leaves unmounts the tooltip — and both of these fire exactly when
 * a control leaves (the drawer button hides once its drawer opens; the export tabs go with their popup).
 * Living in the tooltip, they died with it and left the overlay up with no tooltip and no way on.
 *
 * Returns what the tooltip calls when its Next presses the opener, so that press counts as the cause.
 */
export const useWalkthroughStepAdvance = ({ controls, steps, stepIndex }: Options): ((index: number) => void) => {
	const step = steps[stepIndex];
	const data = walkthroughStepData(step);
	const openerSelector = typeof step?.target === "string" ? step.target : null;
	const revealAnchor = data?.revealAnchor ?? null;
	const deferredAnchor = data?.isDeferred === true ? data.anchor : null;
	const nextAnchor = data?.nextAnchor ?? null;
	// The step on which the USER (or Next, on their behalf) acted on the opener while its target was closed.
	const causedAtRef = useRef<number | null>(null);
	// The step on which Next pressed the opener while its target was closed: the only cause that counts for a
	// target marked as arriving on its own, where touching the opener reveals nothing.
	const nextCausedAtRef = useRef<number | null>(null);

	const onNextOpens = useCallback((index: number): void => {
		causedAtRef.current = index;
		nextCausedAtRef.current = index;
	}, []);

	// Capture phase on the document, so it sees the event before the control's handler mounts the target.
	// Checking the target is still closed keeps a click on an already-open opener from arming a later advance.
	useEffect(() => {
		if (revealAnchor === null || openerSelector === null) return;
		const markIfCaused = (event: Event): void => {
			const opener = document.querySelector(openerSelector);
			if (opener === null || !(event.target instanceof Node) || !opener.contains(event.target)) return;
			if (!isAnchorVisible(revealAnchor)) causedAtRef.current = stepIndex;
		};
		const onKeyDown = (event: KeyboardEvent): void => {
			if (ACTIVATION_KEYS.has(event.key)) markIfCaused(event);
		};
		document.addEventListener("pointerdown", markIfCaused, true);
		document.addEventListener("click", markIfCaused, true);
		document.addEventListener("keydown", onKeyDown, true);
		return () => {
			document.removeEventListener("pointerdown", markIfCaused, true);
			document.removeEventListener("click", markIfCaused, true);
			document.removeEventListener("keydown", onKeyDown, true);
		};
	}, [revealAnchor, openerSelector, stepIndex]);

	// Advances on CAUSE, not mere arrival. The target must have been absent during this step: finding it open
	// means the user opened it earlier, and advancing would skip this step's copy unread — but one that flashed
	// and closed still counts when it returns. And the opener must have been pressed while it was closed; for
	// a target marked as arriving on its own (the gate-held replacement wrapper), only Next counts.
	useEffect(() => {
		if (controls === null || revealAnchor === null) return;
		let sawAbsent = !isAnchorVisible(revealAnchor);
		let cancelSettle: (() => void) | null = null;
		const observer = new MutationObserver(() => {
			if (!isAnchorVisible(revealAnchor)) {
				sawAbsent = true;
				return;
			}
			if (!sawAbsent) return;
			const selfArriving = document.querySelector(anchorSelector(revealAnchor))?.hasAttribute(WALKTHROUGH_ARRIVES_ON_ITS_OWN_ATTR) === true;
			if ((selfArriving ? nextCausedAtRef : causedAtRef).current !== stepIndex) return;
			observer.disconnect();
			// Not the instant it mounts: joyride measures the next target once, where it STARTED sliding in.
			cancelSettle = whenAnchorSettles(revealAnchor, () => advanceFrom(controls, stepIndex));
		});
		observer.observe(document.body, ANCHOR_ARRIVAL_MUTATIONS);
		return () => {
			observer.disconnect();
			cancelSettle?.();
			// A cause answers one visit of one step: Back onto it, or a new tour starting on it, starts clean.
			causedAtRef.current = null;
			nextCausedAtRef.current = null;
		};
	}, [controls, revealAnchor, stepIndex]);

	// Advance only if there is somewhere to advance TO. When the next control lived in the surface that just
	// closed, moving on would grind through a cutout-less wait per step; ending is the honest outcome.
	useEffect(() => {
		if (controls === null || deferredAnchor === null) return;
		return watchAnchor(deferredAnchor, () => {
			if (controls.info().index !== stepIndex) return;
			if (nextAnchor === null || !isAnchorVisible(nextAnchor)) controls.skip();
			else controls.next();
		});
	}, [controls, deferredAnchor, nextAnchor, stepIndex]);

	return onNextOpens;
};
